/**
 * `/raporty/katalog` — one read, the narrowing the trainer set, and the three writes the shelf takes.
 *
 * It decides nothing: `model/questionCatalog` says what a row is, what the filters hide and what the
 * trash has to state. The shelf is read once and narrowed here rather than re-requested per filter —
 * the route takes no parameters, and a request per facet would print a count of a list that had
 * already been cut.
 *
 * ── The EXPOSED state is the wait branch, tail and all ───────────────────────────────────────
 * `state` says „pending" for as long as the gate says the wait belongs on screen, which outlasts the
 * read by the skeleton's 300ms minimum life. The screen forks on it and so forks on the gate, and
 * the rows that replace the bars carry the reveal in their first render. `shelf.isPending` is the
 * read and stays below, unexposed.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  useDeleteSurveyQuestionCatalogId,
  usePatchSurveyQuestionCatalogId,
  usePostSurveyQuestionCatalog,
} from '~/shared/api/generated/endpoints/survey/survey';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';

import { questionCatalogQueryOptions, questionCatalogRoot } from '../api/shelfQueries';
import type { AuthorableAnswerType } from '../model/editorDraft';
import {
  NO_SHELF_FACETS,
  filterShelf,
  shelfAnswerTypes,
  shelfIsNarrowed,
  shelfQuestions,
  type ShelfFacets,
  type ShelfQuestion,
} from '../model/questionCatalog';

/** What the shelf is showing. Four answers, and `rows` is reached from exactly one of them. */
export type ShelfState = 'pending' | 'error' | 'empty' | 'noResults' | 'rows';

/** What the editor's „Nowe pytanie" card and the shelf screen both write. */
export interface ShelfDraft {
  readonly label: string;
  readonly answerType: AuthorableAnswerType;
  readonly options: readonly string[];
  readonly required: boolean;
}

export interface QuestionShelfView {
  /** What the shelf draws. „pending" covers the whole gated wait, not only the open read. */
  readonly state: ShelfState;
  readonly skeleton: boolean;
  readonly reveal: string;
  readonly rows: readonly ShelfQuestion[];
  readonly total: number;
  readonly answerTypes: readonly string[];
  readonly facets: ShelfFacets;
  readonly setFacets: (facets: ShelfFacets) => void;
  readonly narrowed: boolean;
  readonly saving: boolean;
  readonly writeFailed: boolean;
  /**
   * File a question on the shelf. `onDone` is handed the id the server minted, because the CALLER
   * has to record it: the editor's bookmark reads „already filed" off `sourceQuestionId`, and a
   * press whose id is dropped leaves the control offering to file the same question again.
   */
  readonly create: (draft: ShelfDraft, onDone?: (id: number | null) => void) => void;
  readonly update: (id: number, draft: ShelfDraft, onDone?: () => void) => void;
  readonly remove: (id: number, onDone?: () => void) => void;
  readonly onRetry: () => void;
}

export function useQuestionShelf(): QuestionShelfView {
  const queryClient = useQueryClient();
  const shelf = useQuery(questionCatalogQueryOptions());
  const [facets, setFacets] = useState<ShelfFacets>(NO_SHELF_FACETS);
  const [writeFailed, setWriteFailed] = useState(false);

  const create = usePostSurveyQuestionCatalog();
  const patch = usePatchSurveyQuestionCatalogId();
  const drop = useDeleteSurveyQuestionCatalogId();

  const all = shelfQuestions(shelf.data);
  const rows = filterShelf(all, facets);
  const narrowed = shelfIsNarrowed(facets);

  const answer: ShelfState = shelf.isPending
    ? 'pending'
    : shelf.isError
      ? 'error'
      : rows.length > 0
        ? 'rows'
        : narrowed
          ? 'noResults'
          : 'empty';
  const gate = useSkeletonGate(answer === 'pending');
  const state: ShelfState = gate.waiting ? 'pending' : answer;

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: questionCatalogRoot });
  };

  /**
   * A shelf entry's body, as both writes take it.
   *
   * `isHealthData` is never sent. The consent gate keys on the STANDARD catalog's own codes, so the
   * flag on a trainer's own question gates nothing — and `SurveyTemplateItemInput` carries no such
   * field at all, so a question marked here would lose the mark the moment it entered a survey. See
   * `AGENTS.md`; the chip is a deliberate omission rather than an oversight.
   *
   * `unit` is never sent either, for the same reason and with one more: it is OMITTED rather than
   * sent as `null`, so a PATCH cannot wipe a unit an older build stored on a row this app can no
   * longer show.
   */
  const bodyOf = (draft: ShelfDraft) => ({
    label: draft.label.trim(),
    answerType: draft.answerType,
    options: draft.options.length > 0 ? [...draft.options] : null,
    required: draft.required,
  });

  return {
    state,
    skeleton: gate.skeleton,
    reveal: gate.reveal,
    rows,
    total: all.length,
    answerTypes: shelfAnswerTypes(all),
    facets,
    setFacets,
    narrowed,
    saving: create.isPending || patch.isPending || drop.isPending,
    writeFailed,
    create: (draft, onDone) => {
      setWriteFailed(false);
      create.mutate(
        { data: bodyOf(draft) },
        {
          onSuccess: (response) => { invalidate(); onDone?.(response.id ?? null); },
          onError: () => { setWriteFailed(true); },
        },
      );
    },
    update: (id, draft, onDone) => {
      setWriteFailed(false);
      patch.mutate(
        { id, data: bodyOf(draft) },
        {
          onSuccess: () => { invalidate(); onDone?.(); },
          onError: () => { setWriteFailed(true); },
        },
      );
    },
    remove: (id, onDone) => {
      setWriteFailed(false);
      drop.mutate(
        { id },
        {
          onSuccess: () => { invalidate(); onDone?.(); },
          onError: () => { setWriteFailed(true); },
        },
      );
    },
    onRetry: () => { void shelf.refetch(); },
  };
}
